import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Plus, PencilSimple, Trash, MagnifyingGlass, ArrowUp, ArrowDown, ArrowsDownUp, Export, Paperclip, FileArrowUp } from "@phosphor-icons/react";
import { useTableFeatures, exportCSV } from "@/hooks/useTableFeatures";
import { useBulkSelect } from "@/hooks/useBulkSelect";
import BulkActionBar from "@/components/BulkActionBar";
import { Checkbox } from "@/components/ui/checkbox";

const defaultForm = {
  client_id: "", client_name: "", title: "", nickname: "", service_provider: "", spin: "",
  service_type: "category_1", start_date: "", end_date: "", contract_value: 0, status: "active", notes: "",
};

const statusColors = { active: "bg-[#00B341]/10 text-[#00B341] border-[#00B341]/20", expired: "bg-gray-100 text-[#6B7280] border-gray-200", pending: "bg-[#FFD100]/10 text-[#946B00] border-[#FFD100]/20" };

export default function Contracts() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...defaultForm });
  const [yearFilter, setYearFilter] = useState("all");
  const [syncing, setSyncing] = useState(false);
  const bulk = useBulkSelect();
  const bulkComplete = () => { bulk.clear(); load(); };
  const { search, setSearch, sortKey, sortDir, toggleSort, filters, setFilter, clearFilters, processed, activeFilterCount } = useTableFeatures(items);
  const csvCols = [
    { label: "Client", key: "client_name" }, { label: "Contract ID", key: "title" }, { label: "Nickname", key: "nickname" }, { label: "Provider", key: "service_provider" },
    { label: "Type", key: "service_type" }, { label: "Award Date", key: "start_date" }, { label: "Expiration Date", key: "end_date" },
    { label: "Value", key: "contract_value" }, { label: "Status", key: "status" },
  ];
  const SH = ({ k, children, className = "" }) => (
    <TableHead className={`py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold cursor-pointer select-none hover:text-[#1A1D20] ${className}`} onClick={() => toggleSort(k)}>
      <div className="flex items-center gap-1">{children}{sortKey === k ? (sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ArrowsDownUp size={10} className="opacity-30" />}</div>
    </TableHead>
  );

  useEffect(() => { load(); loadClients(); }, []);

  const load = async () => { try { const { data } = await api.get("/contracts"); setItems(data); } catch { toast.error("Failed to load"); } };
  const loadClients = async () => { try { const { data } = await api.get("/clients"); setClients(data); } catch {} };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Contract ID is required"); return; }
    try {
      if (editing) { await api.put(`/contracts/${editing}`, form); toast.success("Updated"); }
      else { await api.post("/contracts", form); toast.success("Created"); }
      setOpen(false); load();
    } catch { toast.error("Failed to save"); }
  };

  const syncFromUSAC = async () => {
    if (!window.confirm("Import contracts from USAC EPC for all clients? This may take a minute.")) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/contracts/sync-usac");
      toast.success(`Imported ${data.imported} contracts from ${data.clients_checked} clients`);
      load();
    } catch { toast.error("USAC sync failed"); }
    finally { setSyncing(false); }
  };

  const importCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/contracts/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported}${data.skipped ? `, skipped ${data.skipped}` : ""}`);
      load();
    } catch { toast.error("CSV import failed"); }
    e.target.value = "";
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/contracts/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url; a.download = "contracts_template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download template"); }
  };

  const remove = async (id) => { if (!window.confirm("Delete?")) return; try { await api.delete(`/contracts/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };
  const edit = (item) => { setForm({ ...item }); setEditing(item.id); setOpen(true); };
  const openNew = () => { setForm({ ...defaultForm }); setEditing(null); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadAttachment = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/attachments/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, attachments: [...(f.attachments || []), data] }));
      toast.success(`Attached: ${file.name}`);
    } catch { toast.error("Upload failed"); }
  };
  const removeAttachment = (idx) => setForm((f) => ({ ...f, attachments: (f.attachments || []).filter((_, i) => i !== idx) }));

  const selectClient = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    setForm((f) => ({ ...f, client_id: clientId, client_name: client ? client.name : "" }));
  };

  const filtered = yearFilter === "all" ? processed : processed.filter((c) => (c.start_date || "").startsWith(yearFilter));

  return (
    <div className="p-6" data-testid="contracts-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-1">Management</p>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>E-Rate Contracts</h1>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="download-contract-template-btn" onClick={downloadTemplate} className="bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-3 py-2 text-xs flex items-center gap-1.5 transition-colors">
            <Export size={14} weight="bold" /> Template
          </button>
          <label data-testid="import-contract-csv-btn" className="cursor-pointer bg-transparent text-[#002FA7] border border-[#002FA7] hover:bg-[#002FA7]/5 rounded-none font-medium px-3 py-2 text-xs flex items-center gap-1.5 transition-colors">
            <FileArrowUp size={14} weight="bold" /> Import CSV
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={importCSV} />
          </label>
          <button data-testid="sync-usac-contracts-btn" onClick={syncFromUSAC} disabled={syncing} className="bg-transparent text-[#002FA7] border border-[#002FA7] hover:bg-[#002FA7]/5 disabled:opacity-50 rounded-none font-medium px-4 py-2 text-sm flex items-center gap-2 transition-colors">
            <FileArrowUp size={16} weight="bold" /> {syncing ? "Syncing..." : "Import from USAC"}
          </button>
          <button data-testid="add-contract-btn" onClick={openNew} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm flex items-center gap-2 transition-colors">
            <Plus size={16} weight="bold" /> Add Contract
          </button>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <Input data-testid="search-contracts" placeholder="Search contracts..." value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-none pl-9" />
        </div>
        <Select value={filters.status || "all"} onValueChange={(v) => setFilter("status", v)}>
          <SelectTrigger data-testid="filter-status" className="rounded-none w-[130px]"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent className="rounded-none"><SelectItem value="all">All Status</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="expired">Expired</SelectItem></SelectContent>
        </Select>
        <Select value={filters.service_type || "all"} onValueChange={(v) => setFilter("service_type", v)}>
          <SelectTrigger data-testid="filter-cat" className="rounded-none w-[140px]"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent className="rounded-none"><SelectItem value="all">All Categories</SelectItem><SelectItem value="category_1">Category 1</SelectItem><SelectItem value="category_2">Category 2</SelectItem></SelectContent>
        </Select>
        <Select value={yearFilter} onValueChange={setYearFilter}>
          <SelectTrigger data-testid="filter-contract-year" className="rounded-none w-[140px]"><SelectValue placeholder="All Years" /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all">All Years</SelectItem>
            {[2026, 2027, 2028, 2029, 2030].map((y) => <SelectItem key={y} value={String(y)}>FY {y}</SelectItem>)}
          </SelectContent>
        </Select>
        {activeFilterCount > 0 && <button onClick={clearFilters} className="text-xs text-[#002FA7] hover:underline">Clear</button>}
        <button data-testid="export-contracts-btn" onClick={() => exportCSV(filtered, csvCols, "contracts.csv")} className="ml-auto flex items-center gap-1.5 bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-3 py-2 text-xs transition-colors">
          <Export size={14} /> Export CSV
        </button>
      </div>

      <div className="border border-gray-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="data-table-header">
              <TableHead className="py-3 px-3 w-[40px]"><Checkbox data-testid="select-all-contracts" checked={bulk.selected.size === filtered.length && filtered.length > 0} onCheckedChange={() => bulk.toggleAll(filtered.map(i => i.id))} /></TableHead>
              <SH k="client_name">Client</SH>
              <SH k="title">Contract ID</SH>
              <SH k="service_provider">Provider</SH>
              <SH k="nickname">Contract Nickname</SH>
              <SH k="start_date">Award Date</SH>
              <SH k="end_date">Expiration Date</SH>
              <SH k="contract_value">Value</SH>
              <SH k="status">Status</SH>
              <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold w-[120px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-8 text-[#6B7280] text-sm">No contracts found</TableCell></TableRow>
            ) : filtered.map((item) => (
              <TableRow key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                <TableCell className="py-3 px-3"><Checkbox data-testid={`select-contract-${item.id}`} checked={bulk.selected.has(item.id)} onCheckedChange={() => bulk.toggle(item.id)} /></TableCell>
                <TableCell className="py-3 px-4 text-[#1A1D20]">{item.client_name || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-[#1A1D20] font-mono text-xs">{item.title || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-sm">{item.service_provider || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-sm">{item.nickname || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-xs text-[#6B7280]">{item.start_date || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-xs text-[#6B7280]">{item.end_date || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-sm font-medium">${item.contract_value?.toLocaleString() || 0}</TableCell>
                <TableCell className="py-3 px-4"><Badge className={`rounded-none text-xs border ${statusColors[item.status] || statusColors.pending}`}>{item.status}</Badge></TableCell>
                <TableCell className="py-3 px-4">
                  <div className="flex items-center gap-1.5">
                    <button data-testid={`edit-contract-${item.id}`} onClick={() => edit(item)} className="bg-transparent text-[#002FA7] border border-[#002FA7] hover:bg-[#002FA7]/5 rounded-none font-medium px-2.5 py-1 text-xs flex items-center gap-1 transition-colors">
                      <PencilSimple size={12} weight="bold" /> Edit
                    </button>
                    <button data-testid={`delete-contract-${item.id}`} onClick={() => remove(item.id)} className="p-1.5 hover:bg-red-50"><Trash size={14} className="text-[#FF2A00]" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <BulkActionBar count={bulk.count} collection="contracts" selectedIds={bulk.selected} onComplete={bulkComplete}
        statuses={[{value:"active",label:"Active"},{value:"pending",label:"Pending"},{value:"expired",label:"Expired"}]} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-[640px] max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{editing ? "Edit" : "Add"} Contract</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Client</label>
              <Select value={form.client_id} onValueChange={selectClient}>
                <SelectTrigger data-testid="contract-client-select" className="rounded-none"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent className="rounded-none max-h-[200px]">{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Contract ID *</label>
              <Input data-testid="contract-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} className="rounded-none" placeholder="USAC Contract ID" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Contract Nickname</label>
              <Input data-testid="contract-nickname-input" value={form.nickname} onChange={(e) => set("nickname", e.target.value)} className="rounded-none" placeholder="e.g. Cox Fiber 2026-2030" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Service Provider</label>
                <Input data-testid="contract-provider-input" value={form.service_provider} onChange={(e) => set("service_provider", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">SPIN</label>
                <Input data-testid="contract-spin-input" value={form.spin} onChange={(e) => set("spin", e.target.value)} className="rounded-none" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Service Type</label>
                <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}>
                  <SelectTrigger data-testid="contract-type-select" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none"><SelectItem value="category_1">Category 1</SelectItem><SelectItem value="category_2">Category 2</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Award Date</label>
                <Input data-testid="contract-start-input" type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Expiration Date</label>
                <Input data-testid="contract-end-input" type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className="rounded-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Contract Value ($)</label>
                <Input data-testid="contract-value-input" type="number" value={form.contract_value} onChange={(e) => set("contract_value", parseFloat(e.target.value) || 0)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Status</label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="contract-status-select" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none"><SelectItem value="active">Active</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="expired">Expired</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Notes</label>
              <textarea data-testid="contract-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="w-full border border-gray-300 rounded-none px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] min-h-[60px]" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Attachments</label>
              <div className="space-y-2">
                {(form.attachments || []).map((att, i) => (
                  <div key={i} className="flex items-center justify-between border border-gray-200 px-3 py-2 text-sm">
                    <div className="flex items-center gap-2"><Paperclip size={14} className="text-[#002FA7]" /><span>{att.original_filename}</span></div>
                    <button type="button" onClick={() => removeAttachment(i)} className="text-[#FF2A00] text-xs hover:underline">Remove</button>
                  </div>
                ))}
                <label className="flex items-center gap-2 cursor-pointer border border-dashed border-gray-300 px-3 py-2 text-sm text-[#6B7280] hover:border-[#002FA7]/50 transition-colors">
                  <FileArrowUp size={16} /> Upload document
                  <input type="file" className="hidden" onChange={uploadAttachment} data-testid="contract-file-upload" />
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <button data-testid="cancel-contract-btn" onClick={() => setOpen(false)} className="bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-4 py-2 text-sm">Cancel</button>
            <button data-testid="save-contract-btn" onClick={save} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
